import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../context/ApiContext";
import Button from "./ui/Button";
import "./MemoryExplorer.css";

/**
 * Carte unique « Mémoire », deux modes.
 *
 * POURQUOI LA FUSION
 * ------------------
 * L'écran présentait deux cartes qui réclamaient les mêmes quatre filtres :
 * l'une parcourait la mémoire, l'autre y cherchait des voisins. À l'usage, la
 * seconde semblait faire la même chose en moins bien — et elle paraissait même
 * cassée, parce que la collection testée était vide.
 *
 * Elles ne répondent pourtant pas à la même question :
 *  • Parcourir — « qu'y a-t-il là-dedans ? » Filtre exact sur le payload,
 *    seul mode où l'on peut supprimer une entrée.
 *  • Simuler   — « que verra la traduction pour CETTE phrase ? » Recherche
 *    vectorielle, avec les scores. C'est la seule façon de vérifier ce que le
 *    site remontera avant de lancer une traduction.
 *
 * D'où une seule carte, une seule rangée de filtres, deux modes.
 */
export default function MemoryExplorer() {
    const { apiFetch } = useApi();

    const [mode, setMode] = useState("browse"); // browse | simulate

    const [collections, setCollections] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [supports, setSupports] = useState([]);

    const [collection, setCollection] = useState("");
    const [language, setLanguage] = useState("");
    const [support, setSupport] = useState("");
    const [country, setCountry] = useState("");
    const [text, setText] = useState("");
    const [query, setQuery] = useState("");

    const [items, setItems] = useState(null);
    const [selection, setSelection] = useState(() => new Set());
    const [meta, setMeta] = useState(null); // {total, truncated, limit}
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        apiFetch("/collections").then(r => r.json()).then(j => j.success && setCollections(j.data));
        apiFetch("/languages").then(r => r.json()).then(j => j.success && setLanguages(j.data));
        apiFetch("/supports").then(r => r.json()).then(j => j.success && setSupports(j.data));
    }, [apiFetch]);

    const browse = useCallback(async (filters) => {
        if (!filters.collection) {
            setItems(null);
            setMeta(null);
            return;
        }
        setBusy(true);
        setError("");
        try {
            const qs = new URLSearchParams(filters).toString();
            const res = await apiFetch(`/vectors?${qs}`).then(r => r.json());
            if (res.success) {
                setItems(res.data);
                setSelection(new Set()); // une nouvelle liste invalide l'ancienne selection
                setMeta({ total: res.total, truncated: res.truncated, limit: res.limit });
            } else {
                setError(res.error || "Erreur API");
            }
        } catch (err) {
            setError("Impossible de contacter l'API : " + err.message);
        } finally {
            setBusy(false);
        }
    }, [apiFetch]);

    /* Changer de collection recharge sans filtre : on voit d'abord tout. */
    useEffect(() => {
        if (mode !== "browse") {
            return;
        }
        browse({ collection, language: "", support: "", country: "", text: "" });
    }, [collection, mode, browse]);

    const simulate = async () => {
        if (!query.trim() || !collection) {
            setError("Choisissez une collection et saisissez une phrase.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const qs = new URLSearchParams({
                q: query, collection, language, support, country, k: 40,
            }).toString();
            const res = await apiFetch(`/nearest?${qs}`).then(r => r.json());
            if (res.success) {
                setItems(res.data);
                setSelection(new Set());
                setMeta(null);
            } else {
                setError(res.error || "Erreur API");
            }
        } catch (err) {
            setError("Impossible de contacter l'API : " + err.message);
        } finally {
            setBusy(false);
        }
    };

    const submit = (e) => {
        e.preventDefault();
        if (mode === "browse") {
            browse({ collection, language, support, country, text });
        } else {
            simulate();
        }
    };

    const switchMode = (next) => {
        setMode(next);
        setItems(null);
        setSelection(new Set());
        setMeta(null);
        setError("");
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette entrée de la mémoire ?")) return;
        await apiFetch(`/vectors/${id}?collection=${collection}`, { method: "DELETE" });
        setItems(v => v.filter(x => x.id !== id));
        setSelection(s => { const n = new Set(s); n.delete(id); return n; });
    };

    const bascule = (id) => setSelection(s => {
        const n = new Set(s);
        if (n.has(id)) {
            n.delete(id);
        } else {
            n.add(id);
        }
        return n;
    });

    const toutSelectionner = (coche) => {
        setSelection(coche ? new Set((items || []).map(i => i.id)) : new Set());
    };

    /**
     * Suppression groupée.
     *
     * Purger une brochure porte sur une centaine d'entrées : les supprimer une
     * par une, c'est autant d'allers-retours et un risque d'échec à mi-chemin.
     * L'API les traite en un seul appel.
     */
    const supprimerSelection = async () => {
        const ids = Array.from(selection);
        if (ids.length === 0) {
            return;
        }
        // La suppression est irréversible et porte ici sur plusieurs entrées :
        // on annonce le nombre exact plutôt qu'un « êtes-vous sûr ? ».
        if (!window.confirm(
            `Supprimer définitivement ${ids.length} entrée(s) de la mémoire « ${collection} » ?`
        )) {
            return;
        }

        setBusy(true);
        setError("");
        try {
            const res = await apiFetch("/vectors/delete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ collection, ids }),
            }).then(r => r.json());

            if (!res.success) {
                setError(res.error || "La suppression a échoué.");
                return;
            }
            const supprimes = new Set(ids);
            setItems(v => v.filter(x => !supprimes.has(x.id)));
            setSelection(new Set());
            setMeta(m => (m && m.total != null ? { ...m, total: m.total - ids.length } : m));
        } catch (err) {
            setError("Impossible de contacter l'API : " + err.message);
        } finally {
            setBusy(false);
        }
    };

    /* Résumé honnête : on distingue ce qui est affiché de ce qui existe. */
    let resume = "Choisissez une collection";
    if (items && mode === "simulate") {
        resume = `${items.length} voisin(s)`;
    } else if (items && meta) {
        resume = meta.total != null && meta.total > items.length
            ? `${items.length} affichée(s) sur ${meta.total}`
            : `${items.length} entrée(s)`;
    } else if (items) {
        resume = `${items.length} entrée(s)`;
    }

    return (
        <section className="card" id="memory">
            <div className="card__head">
                <h2>Mémoire</h2>
                <p>{resume}</p>
            </div>

            <nav className="memory-modes" aria-label="Mode de consultation">
                <button type="button" className={mode === "browse" ? "is-active" : ""}
                        aria-current={mode === "browse" ? "true" : undefined}
                        onClick={() => switchMode("browse")}>
                    Parcourir
                </button>
                <button type="button" className={mode === "simulate" ? "is-active" : ""}
                        aria-current={mode === "simulate" ? "true" : undefined}
                        onClick={() => switchMode("simulate")}>
                    Simuler une traduction
                </button>
            </nav>

            <div className="card__body">
                <form className="form-grid" onSubmit={submit}>
                    {mode === "simulate" && (
                        <div className="field field--wide">
                            <label htmlFor="mem-query">Phrase source</label>
                            <textarea id="mem-query" rows={2} placeholder="Tapez la phrase à traduire…"
                                      value={query} onChange={e => setQuery(e.target.value)} />
                            <span className="field__hint">
                                Mêmes filtres que le site : ce qui remonte ici est ce que la
                                traduction recevra comme exemples.
                            </span>
                        </div>
                    )}

                    <div className="field">
                        <label htmlFor="mem-collection">Collection</label>
                        <select id="mem-collection" value={collection}
                                onChange={e => setCollection(e.target.value)}>
                            <option value="">—</option>
                            {collections.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    <div className="field">
                        <label htmlFor="mem-language">Langue</label>
                        <select id="mem-language" value={language} onChange={e => setLanguage(e.target.value)}>
                            <option value="">Toutes</option>
                            {languages.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>

                    <div className="field">
                        <label htmlFor="mem-support">Support</label>
                        <select id="mem-support" value={support} onChange={e => setSupport(e.target.value)}>
                            <option value="">Tous</option>
                            {supports.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <div className="field">
                        <label htmlFor="mem-country">Pays</label>
                        <input id="mem-country" type="text" placeholder="Tous" value={country}
                               onChange={e => setCountry(e.target.value)} />
                    </div>

                    {mode === "browse" && (
                        <div className="field field--wide">
                            <label htmlFor="mem-text">Contient</label>
                            <input id="mem-text" type="text" placeholder="Filtre texte" value={text}
                                   onChange={e => setText(e.target.value)} />
                        </div>
                    )}

                    {error && <p className="notice notice--error field--wide">{error}</p>}

                    <div className="form-actions">
                        <Button variant={mode === "browse" ? "secondary" : "primary"}
                                type="submit" disabled={busy}>
                            {busy ? "Chargement…" : (mode === "browse" ? "Filtrer" : "Simuler")}
                        </Button>
                    </div>
                </form>

                {meta?.truncated && (
                    <p className="notice notice--warn memory-truncated">
                        Affichage limité à {meta.limit} entrées : la collection en contient
                        davantage. Affinez les filtres pour être sûr de tout voir.
                    </p>
                )}

                {items !== null && items.length === 0 && (
                    <p className="empty">
                        {mode === "browse"
                            ? "Aucune entrée pour ces filtres."
                            : "Aucun voisin. Vérifiez la langue et le support : un texte indexé en brochure ne remonte pas si le filtre est sur site."}
                    </p>
                )}

                {mode === "browse" && items !== null && items.length > 0 && (
                    <div className="memory-bulk">
                        <label className="memory-bulk__all">
                            <input
                                type="checkbox"
                                checked={selection.size === items.length && items.length > 0}
                                ref={el => {
                                    if (el) {
                                        el.indeterminate = selection.size > 0 && selection.size < items.length;
                                    }
                                }}
                                onChange={e => toutSelectionner(e.target.checked)}
                            />
                            <span>
                                {selection.size === 0
                                    ? `Tout sélectionner (${items.length} affichée(s))`
                                    : `${selection.size} sélectionnée(s) sur ${items.length}`}
                            </span>
                        </label>

                        {selection.size > 0 && (
                            <Button variant="danger" size="sm" type="button"
                                    disabled={busy} onClick={supprimerSelection}>
                                Supprimer {selection.size} entrée(s)
                            </Button>
                        )}
                    </div>
                )}

                {/* La sélection ne porte que sur ce qui est AFFICHÉ : le dire
                    évite de croire qu'on purge toute la collection. */}
                {mode === "browse" && meta?.truncated && selection.size > 0 && (
                    <p className="field__hint memory-bulk__note">
                        La sélection ne concerne que les entrées affichées, pas les
                        {" "}{meta.total != null ? meta.total - items.length : "autres"} restantes.
                    </p>
                )}

                {items !== null && items.length > 0 && (
                    <ul className="memory-list scroller">
                        {items.map(item => (
                            <li key={item.id}
                                className={`memory-item${selection.has(item.id) ? " is-selected" : ""}`}>
                                {mode === "browse" && (
                                    <input
                                        type="checkbox"
                                        className="memory-item__check"
                                        checked={selection.has(item.id)}
                                        onChange={() => bascule(item.id)}
                                        aria-label="Sélectionner cette entrée"
                                    />
                                )}
                                <div className="memory-item__main">
                                    <div className="memory-tags">
                                        {item.score != null && (
                                            <span className="memory-score">{item.score.toFixed(3)}</span>
                                        )}
                                        <span className="tag">{item.payload.support}</span>
                                        {item.payload.language && <span className="tag">{item.payload.language}</span>}
                                        {item.payload.country && <span className="tag">{item.payload.country}</span>}
                                        {item.payload.campaign && <span className="tag">{item.payload.campaign}</span>}
                                        {/* Une paire porte son texte source : c'est ce qui autorise
                                            le RÉEMPLOI d'une formulation, pas seulement l'inspiration. */}
                                        {item.payload.source_text && <span className="tag tag--brand">paire</span>}
                                    </div>
                                    <p className="memory-text">{item.payload.text}</p>
                                    {item.payload.source_text && (
                                        <p className="memory-source">
                                            <span>{item.payload.source_lang || "source"}</span>
                                            {item.payload.source_text}
                                        </p>
                                    )}
                                </div>
                                {mode === "browse" && (
                                    <Button size="sm" variant="ghost" onClick={() => del(item.id)}>
                                        Supprimer
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
